import { inject, Injectable, signal } from '@angular/core';
import { CategoriesFe, CategoriesFields, CategoriesForm, CategoriesFormGroups, CategoriesValidators } from '../categories.models';
import { CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { Validators } from '@angular/forms';
import { Categories } from 'tilby-models';
import { VariationsFormService } from '../../items-tab/item-details/item-details-tabs/item-variations-section/variations/variations-form.service';

@Injectable()
export class CategoryDetailsFormService {

  readonly variationsFormService = inject(VariationsFormService);
  
  private categoryForm!: CategoriesFormGroups;
  categoryFormReady = signal(false);

  get form() {
    return this.categoryForm;
  }

  validators: CategoriesValidators = {
    nameMaxLength: 255,
    scaleCategoryMin: 1,
    scaleCategoryMax: 50,

    // --------
    provMinLength: 0,
    provMaxLength: 255,
    provMaxLengthArray: new Array(10).fill(2)
  };

  createForm(category: CategoriesFields = new CategoriesFe() ,hiddenFields?:Array<keyof CategoriesFields>) {
    this.categoryForm = new CustomFormGroup<CategoriesForm>({
      id: new CustomFormControl(
        category.id || null
      ),
      name: new CustomFormControl(
        category.name || null,
        { validators: [Validators.required, Validators.maxLength(this.validators.nameMaxLength)] }
      ),
      alternative_names: new CustomFormControl(
        (category.alternative_names || []).map(alt => ({...alt}))
      ),
      index: new CustomFormControl(
        category.index || null
      ),
      color: new CustomFormControl(
        category.color || null
      ),
      printer_id: new CustomFormControl(
        category.printer_id || null
      ),
      terminal_id: new CustomFormControl(
        category.terminal_id || null
      ),
      keyboard_code: new CustomFormControl(
        category.keyboard_code || null,
        { validators: [Validators.min(this.validators.scaleCategoryMin), Validators.max(this.validators.scaleCategoryMax)] }
      ),
      department_id: new CustomFormControl(
        category.department_id || null
      ),
      department2_id: new CustomFormControl(
        category.department2_id || null
      ),
      department3_id: new CustomFormControl(
        category.department3_id || null
      ),
      department4_id: new CustomFormControl(
        category.department4_id || null
      ),
      department5_id: new CustomFormControl(
        category.department5_id || null
      ),
      department6_id: new CustomFormControl(
        category.department6_id || null
      ),
      department7_id: new CustomFormControl(
        category.department7_id || null
      ),
      department8_id: new CustomFormControl(
        category.department8_id || null
      ),
      department9_id: new CustomFormControl(
        category.department9_id || null
      ),
      department10_id: new CustomFormControl(
        category.department10_id || null
      ),
      display: new CustomFormControl(
        category.display
      ),
      display_kiosk: new CustomFormControl(
        category.display_kiosk
      ),
      display_slave_device: new CustomFormControl(
        category.display_slave_device
      )
    });

    this.categoryFormReady.set(true);
    return this.categoryForm;
  }

  resetFormValues(category: Categories = new CategoriesFe()) {
    this.categoryForm.patchValue({
      id: category.id || null,
      name: category.name || null,
      alternative_names: (category.alternative_names || []).map(alt => ({...alt})),
      index:  category.index || null,
      color: category.color || null,
      printer_id: category.printer_id || null,
      terminal_id: category.terminal_id || null,
      keyboard_code: category.keyboard_code || null,
      department_id: category.department_id || null,
      department2_id: category.department2_id || null,
      department3_id: category.department3_id || null,
      department4_id: category.department4_id || null,
      department5_id: category.department5_id || null,
      department6_id: category.department6_id || null,
      department7_id: category.department7_id || null,
      department8_id: category.department8_id || null,
      department9_id: category.department9_id || null,
      department10_id: category.department10_id || null,
      display: category.display,
      display_kiosk: category.display_kiosk,
      display_slave_device: category.display_slave_device
    });

    this.categoryForm.markAsPristine();
  }

  getComponentFields(currentCategory: Categories = new CategoriesFe()): Partial<Categories> {
    // Clono l'oggetto di destinazione per non mutare l'originale
    const updatedCategory = { ...currentCategory }; 

    // Recupero tutte le chiavi (nomi dei campi) presenti nel form
    const componentFieldsToMap = Object.keys(this.categoryForm.controls);

    // Ciclo su tutte le chiavi trovate nel form
    componentFieldsToMap.forEach((key: string) => {

      const control = this.categoryForm.get(key);

      if(control) {
        let value = control.value;

        if ((key.startsWith('department') || key.startsWith('printer')) && key.endsWith('_id')) {
          value = (value === 0 || value === '0') ? null : value;
        }

        if (key === 'keyboard_code') {
          value = value || null;
        }

        // Assegno il valore all'oggetto
        (updatedCategory as any)[key] = value
      }
    })

    const variations = this.variationsFormService.getVariationsFields();
    (updatedCategory as any)['variations'] = variations;

    return updatedCategory;
  }

  get id() {
    return this.categoryForm.controls.id;
  }

  get name() {
    return this.categoryForm.controls.name;
  }

  get alternativeNames() {
    return this.categoryForm.controls.alternative_names;
  }

  get index() {
    return this.categoryForm.controls.index;
  }

  get color() {
    return this.categoryForm.controls.color;
  }

  get printer_id() {
    return this.categoryForm.controls.printer_id;
  }

  get terminal_id() {
    return this.categoryForm.controls.terminal_id;
  }

  get keyboard_code() {
    return this.categoryForm.controls.keyboard_code;
  }

  get department_id() {
    return this.categoryForm.controls.department_id;
  }

  get department2_id() {
    return this.categoryForm.controls.department2_id;
  }

  get department3_id() {
    return this.categoryForm.controls.department3_id;
  }

  get department4_id() {
    return this.categoryForm.controls.department4_id;
  }

  get department5_id() {
    return this.categoryForm.controls.department5_id;
  }

  get department6_id() {
    return this.categoryForm.controls.department6_id;
  }

  get department7_id() {
    return this.categoryForm.controls.department7_id;
  }

  get department8_id() {
    return this.categoryForm.controls.department8_id;
  }

  get department9_id() {
    return this.categoryForm.controls.department9_id;
  }

  get department10_id() {
    return this.categoryForm.controls.department10_id;
  }

  get display() {
    return this.categoryForm.controls.display;
  }

  get display_kiosk() {
    return this.categoryForm.controls.display_kiosk;
  }

  get display_slave_device() {
    return this.categoryForm.controls.display_slave_device;
  }
}
